import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CategoryResponse, StatementImportItemResponse, StatementImportTargetGroup, StatementImportTargetInput } from '@clair-avenir/shared';
import { Button } from '@ds/components/core/Button';
import { Card, CardBody, CardHeader } from '@ds/components/core/Card';
import { AuthenticatedShell, Page } from '@/components/AppShell';
import { useToast } from '@/components/ToastProvider';
import { useCategories } from '@/features/categories/api';
import { CategoryFormDialog } from '@/features/categories/components/CategoryFormDialog';
import { useInstitutions } from '@/features/institutions/api';
import {
  useApplyCategoryByHint,
  useCategorizeStatementImport,
  useConfirmStatementImport,
  useDiscardStatementImport,
  useStatementImport,
  useUpdateStatementImportItem,
} from '@/features/statement-imports/api';
import { StatementImportGroupSection } from '@/features/statement-imports/components/StatementImportGroupSection';
import { ApiError } from '@/lib/http';

export const Route = createFileRoute('/_authenticated/importar_/$importId')({ component: StatementImportDetailPage });

function StatementImportDetailPage() {
  const { importId } = Route.useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { data: importData, refetch: refetchImport } = useStatementImport(importId);
  const { data: institutionsData } = useInstitutions(false);
  const { data: categoriesData } = useCategories(false);
  // Um alvo por grupo de destino (0013.2) — quando o arquivo não tem colunas de Origem/
  // Instituição/Conta (caso comum), `targetGroups` tem sempre 1 elemento só (o grupo
  // padrão), então isso se comporta EXATAMENTE como o seletor único da 0013 original.
  const [targetsByGroup, setTargetsByGroup] = useState<Record<string, StatementImportTargetInput | null>>({});
  const [categoryHintDialog, setCategoryHintDialog] = useState<{ hint: string; kind: 'receita' | 'despesa' } | null>(null);

  const accounts = useMemo(() => institutionsData?.flatMap((i) => i.accounts ?? []) ?? [], [institutionsData]);
  const cards = useMemo(() => institutionsData?.flatMap((i) => i.cards ?? []) ?? [], [institutionsData]);
  const categories = categoriesData ?? [];

  // Resolve o alvo EFETIVO de um grupo (0013.3): a escolha manual em `targetsByGroup`
  // quando existir, senão a resolução automática do worker (0013.2) — necessário porque
  // a seção nasce recolhida pro grupo já resolvido (`StatementImportGroupSection`), e o
  // `StatementImportTargetPicker` só emite quando MONTADO (recolhido ele nem monta). Valor
  // derivado a cada render, não estado — sem isso o grupo já resolvido nunca entraria em
  // `targetsByGroup`, travando "Confirmar" pra sempre.
  function resolveGroupTarget(group: StatementImportTargetGroup): StatementImportTargetInput | null {
    const explicit = targetsByGroup[group.groupKey];
    if (explicit !== undefined) return explicit;
    if (group.suggestedAccountId) return { mode: 'existing_account', accountId: group.suggestedAccountId };
    if (group.suggestedCardId) return { mode: 'existing_card', cardId: group.suggestedCardId };
    return null;
  }

  const onError = (title: string) => (error: unknown) => {
    if (error instanceof ApiError) toast({ tone: 'negative', title, description: error.message });
  };
  const updateItem = useUpdateStatementImportItem({ onError: onError('Não foi possível salvar a alteração') });
  const applyCategoryByHint = useApplyCategoryByHint({ onError: onError('Não foi possível aplicar a categoria') });
  const confirmImport = useConfirmStatementImport({
    onSuccess: (result) => {
      toast({ tone: 'positive', title: 'Importação confirmada' });
      // 0013.3: crédito/estorno num grupo de cartão não tem onde entrar no modelo hoje —
      // não trava a confirmação (só esses itens ficam de fora), mas o usuário precisa
      // saber que alguma coisa não entrou, em vez de descobrir sozinho depois.
      if (result.skippedCount > 0) {
        toast({
          tone: 'warning',
          title: `${result.skippedCount} transaç${result.skippedCount === 1 ? 'ão' : 'ões'} não importada${result.skippedCount === 1 ? '' : 's'}`,
          description: 'Crédito/estorno em cartão ainda não tem suporte na importação.',
        });
      }
      // 0013.4 (bug real reportado em produção: reimportar o mesmo extrato duplicava
      // tudo): checagem de segurança no confirm pulou item(ns) que batem com uma
      // transação/parcela já existente — nunca sinalizados na revisão (conta/cartão só
      // resolveu sem ambiguidade aqui no confirm).
      if (result.duplicatesSkipped > 0) {
        toast({
          tone: 'warning',
          title: `${result.duplicatesSkipped} transaç${result.duplicatesSkipped === 1 ? 'ão' : 'ões'} não importada${result.duplicatesSkipped === 1 ? '' : 's'} (possível duplicata)`,
          description: 'Já existe uma transação igual na conta/cartão de destino.',
        });
      }
      navigate({ to: '/transacoes' });
    },
    onError: onError('Não foi possível confirmar a importação'),
  });
  const discardImport = useDiscardStatementImport({ onSuccess: () => navigate({ to: '/importar' }), onError: onError('Não foi possível descartar') });

  // Botão "Categorizar automaticamente": roda em segundo plano no worker (chamadas de LLM
  // levam de dezenas de segundos a minutos, nunca síncrono numa requisição HTTP) — poll
  // curto aqui até as categorias aparecerem, ou até ~2min (sem resposta, para de tentar
  // sozinho; usuário pode clicar de novo ou categorizar na mão).
  const [categorizing, setCategorizing] = useState(false);
  const pollCountRef = useRef(0);
  const categorizeImport = useCategorizeStatementImport({
    onSuccess: () => {
      pollCountRef.current = 0;
      setCategorizing(true);
    },
    onError: onError('Não foi possível iniciar a categorização automática'),
  });
  useEffect(() => {
    if (!categorizing) return;
    const interval = setInterval(() => {
      void (async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current > 40) {
          setCategorizing(false);
          return;
        }
        // `setCategorizing` aqui reage à resposta do refetch (sistema externo), não roda
        // síncrono no corpo do efeito — evita o cascading-render que o lint acusa quando
        // o setState acontece direto na execução do efeito.
        const { data } = await refetchImport();
        const stillMissing = data?.items.some((i) => i.included && !i.suggestedCategoryId);
        if (!stillMissing) setCategorizing(false);
      })();
    }, 3000);
    return () => clearInterval(interval);
  }, [categorizing, refetchImport]);

  if (!importData) return null;

  const missingCategoryCount = importData.items.filter((i) => i.included && !i.suggestedCategoryId).length;
  const allTargetsChosen = importData.targetGroups.every((g) => !!resolveGroupTarget(g));

  // Nome da conta/cartão já vinculado ao grupo de destino (0013.2/0013.3) — reflete a
  // escolha atual (auto-resolvida ou feita à mão), mesmo antes de confirmar a importação.
  function targetLabelFor(group: StatementImportTargetGroup): string | null {
    const target = resolveGroupTarget(group);
    if (!target) return null;
    if (target.mode === 'existing_account') return accounts.find((a) => a.id === target.accountId)?.nickname ?? null;
    if (target.mode === 'existing_card') return cards.find((c) => c.id === target.cardId)?.nickname ?? null;
    return target.nickname || null;
  }

  // Um accordion por grupo (0013.4) — cada seção pagina a própria lista, não existe mais
  // paginação global sobre todos os grupos misturados.
  const itemsByGroup = new Map<string, StatementImportItemResponse[]>();
  for (const item of importData.items) {
    const arr = itemsByGroup.get(item.targetGroupKey) ?? [];
    arr.push(item);
    itemsByGroup.set(item.targetGroupKey, arr);
  }

  function handleCategoryCreated(created: CategoryResponse) {
    if (!categoryHintDialog) return;
    // RN12 (0013.2): aplica a categoria criada a QUALQUER item do MESMO import com o
    // mesmo categoryHint ainda sem categoria — evita repetir "criar categoria" pra cada
    // transação do mesmo nome (ex.: 50 compras de "Farmácias e Drogarias" no arquivo).
    // 1 request só (endpoint dedicado) — nunca 1 PATCH por item: bug real de produção,
    // um extrato com muitas transações do mesmo hint estourava o rate limit da API.
    applyCategoryByHint.mutate({ importId, input: { categoryHint: categoryHintDialog.hint, categoryId: created.id } });
    setCategoryHintDialog(null);
  }

  return (
    <AuthenticatedShell title="Revisar importação">
      <Page>
        <div data-testid="statement-import-detail-page" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gutter-section)' }}>
          {importData.status === 'processando' ? (
            <Card>
              <CardBody>
                <p data-testid="statement-import-processing" style={{ margin: 0 }}>
                  Processando seu extrato...
                </p>
              </CardBody>
            </Card>
          ) : null}

          {importData.status === 'erro' ? (
            <Card>
              <CardBody>
                <p data-testid="statement-import-error" style={{ margin: 0, color: 'var(--feedback-negative-fg)' }}>
                  {importData.errorMessage}
                </p>
                <div style={{ marginTop: 12 }}>
                  <Button variant="danger" data-testid="statement-import-discard" loading={discardImport.isPending} onClick={() => discardImport.mutate(importId)}>
                    Descartar
                  </Button>
                </div>
              </CardBody>
            </Card>
          ) : null}

          {importData.status === 'confirmado' || importData.status === 'descartado' ? (
            <Card>
              <CardBody>
                <p style={{ margin: 0 }}>{importData.status === 'confirmado' ? 'Esta importação já foi confirmada.' : 'Esta importação foi descartada.'}</p>
              </CardBody>
            </Card>
          ) : null}

          {importData.status === 'pronto' ? (
            <>
              <Card>
                <CardHeader
                  title={`Transações (${importData.items.length})`}
                  action={
                    missingCategoryCount > 0 ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid="statement-import-categorize"
                        loading={categorizeImport.isPending || categorizing}
                        onClick={() => categorizeImport.mutate(importId)}
                      >
                        Categorizar automaticamente
                      </Button>
                    ) : undefined
                  }
                />
                {importData.items.length === 0 ? (
                  <CardBody>
                    <p style={{ margin: 0, color: 'var(--text-muted)' }}>Nenhuma transação encontrada neste arquivo.</p>
                  </CardBody>
                ) : null}
              </Card>

              {importData.targetGroups.map((group) => (
                <StatementImportGroupSection
                  key={group.groupKey}
                  group={group}
                  items={itemsByGroup.get(group.groupKey) ?? []}
                  categories={categories}
                  accounts={accounts}
                  cards={cards}
                  targetLabel={targetLabelFor(group)}
                  onTargetChange={(target) => setTargetsByGroup((prev) => ({ ...prev, [group.groupKey]: target }))}
                  onToggleIncluded={(itemId, included) => updateItem.mutate({ importId, itemId, input: { included } })}
                  onCategoryChange={(itemId, categoryId) => updateItem.mutate({ importId, itemId, input: { categoryId } })}
                  onCreateCategoryFromHint={(hint, kind) => setCategoryHintDialog({ hint, kind })}
                />
              ))}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" data-testid="statement-import-discard" loading={discardImport.isPending} onClick={() => discardImport.mutate(importId)}>
                  Descartar
                </Button>
                <Button
                  variant="primary"
                  data-testid="statement-import-confirm"
                  disabled={!allTargetsChosen}
                  loading={confirmImport.isPending}
                  onClick={() => {
                    if (!allTargetsChosen) return;
                    const targets = importData.targetGroups.map((group) => ({ groupKey: group.groupKey, target: resolveGroupTarget(group)! }));
                    confirmImport.mutate({ id: importId, input: { targets } });
                  }}
                >
                  Confirmar importação
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </Page>

      <CategoryFormDialog
        open={!!categoryHintDialog}
        onClose={() => setCategoryHintDialog(null)}
        initialName={categoryHintDialog?.hint}
        initialType={categoryHintDialog?.kind}
        onCreated={handleCategoryCreated}
      />
    </AuthenticatedShell>
  );
}
